import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getErrorMessage } from '../../api/client';
import FormField from '../../components/ui/FormField';
import { validate, required, email } from '../../utils/validation';
import './DemoPages.css';

const DemoRequestPage: React.FC = () => {
  const { t } = useTranslation();
  const [emailValue, setEmailValue] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = (): boolean => {
    const error = validate(emailValue, [
      required(t('demo.validation.emailRequired')),
      email(t('demo.validation.emailInvalid')),
    ]);
    setFieldError(error || '');
    return !error;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await api.public.requestDemo(emailValue);
      setIsSuccess(true);
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="demo-page">
        <div className="demo-container">
          <div className="demo-success">
            <i className="fas fa-envelope-open-text"></i>
            <h1>{t('demo.request.successTitle')}</h1>
            <p>{t('demo.request.successMessage')}</p>
            <p className="demo-email-sent">{emailValue}</p>
            <div className="demo-success-info">
              <i className="fas fa-info-circle"></i>
              <span>{t('demo.request.checkSpam')}</span>
            </div>
            <Link to="/" className="btn btn--secondary">
              {t('demo.request.backToHome')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="demo-page">
      <div className="demo-container">
        <Link to="/" className="demo-back-link">
          <i className="fas fa-arrow-left"></i> {t('demo.backToHome')}
        </Link>

        <div className="demo-card">
          <div className="demo-card-header">
            <i className="fas fa-rocket"></i>
            <h1>{t('demo.request.title')}</h1>
            <p>{t('demo.request.subtitle')}</p>
          </div>

          <div className="demo-features">
            <div className="demo-feature">
              <i className="fas fa-check-circle"></i>
              <span>{t('demo.request.feature1')}</span>
            </div>
            <div className="demo-feature">
              <i className="fas fa-check-circle"></i>
              <span>{t('demo.request.feature2')}</span>
            </div>
            <div className="demo-feature">
              <i className="fas fa-check-circle"></i>
              <span>{t('demo.request.feature3')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="demo-form">
            {submitError && (
              <div className="alert alert-danger" role="alert">
                {submitError}
              </div>
            )}

            <FormField
              label={t('demo.request.emailLabel')}
              htmlFor="demo-email"
              required
              error={fieldError}
            >
              <input
                type="email"
                id="demo-email"
                className="form-control"
                value={emailValue}
                onChange={(e) => {
                  setEmailValue(e.target.value);
                  setFieldError('');
                }}
                placeholder={t('demo.request.emailPlaceholder')}
                disabled={isSubmitting}
                autoFocus
              />
            </FormField>

            <button
              type="submit"
              className="btn btn--primary btn--lg btn--block"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> {t('demo.request.sending')}
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i> {t('demo.request.submit')}
                </>
              )}
            </button>
          </form>

          <p className="demo-privacy">
            {t('demo.request.privacyNotice')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DemoRequestPage;
